// ChatBot.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CopyOutlined, FileAddOutlined, LoadingOutlined } from '@ant-design/icons';
import DOMPurify from 'dompurify';
import 'antd/dist/antd.css'; // Import Ant Design styles
import styles from './ChatBot.module.scss'; // Import the SCSS module 
import { Spin, Button } from 'antd';

interface Message {
  sender?: 'bot' | 'user' | '';
  body: MessageBody[];
  type: string;
  query: string;
}

interface MessageBody {
  text: string;
  header?: string;
  sponsor?: string;
  expanded: boolean;
}

const MAX_WORDS = 80; // Maximum words before truncation

const ChatBot: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [expandedMessages, setExpandedMessages] = useState<Set<number>>(new Set());
  const [isLoading, setLoader] = useState<boolean>(false);
  const [isHighlighted, setIsHighlighted] = useState<boolean>(false);
  const newDivRef = useRef(null);
  const [highlightSection, setHighlightSection] = useState<string>('');
  const [recentResponse, setRecentResponse] = useState<any>();
  const [trialUuid, setTrialUuid] = useState<string>('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const accessTokenRef = useRef<string>('');

  const GENERATE_TEXT = 'Generate selected section';
  const HISTORICAL_REFERENCE_TEXT = 'Show historical references';
  const SPONSOR = "glaxosmithkline";
  // Must match the host page origin exactly (e.g. stage: https://ict.ctd-poc.com). Set via APPURL in add-in build (webpack env / deploy vars.APP_URL).
  const APP_URL = process.env.APPURL;
  const WOPI_URL = process.env.WOPIURL;

  const QUERY_CONFIG = {
    generate: {
      fakeMessage: GENERATE_TEXT,
      endpoint: `${WOPI_URL}/generate`,
      requiresAuth: true,
      buildPayload: (query, trialUuid) => ({
        section: query,
        sponsor: SPONSOR,
        trial_id: trialUuid
      })
    },

    references: {
      fakeMessage: HISTORICAL_REFERENCE_TEXT,
      endpoint: `${WOPI_URL}/get-historical-data`,
      requiresAuth: true,
      buildPayload: (query, trialUuid) => ({
        section_name: query,
        trial_id: trialUuid,
      })
    }
  };

  // Host_TrialData is sent from the host page; event.origin is the host origin. APP_URL must equal that origin or the message is blocked.
  const TRUSTED_ORIGINS = useMemo(() => [
    APP_URL,
    'https://word-edit.officeapps.live.com',
    'https://ffc-word-edit.officeapps.live.com',
    'https://word-edit.officeapps.microsoft.com'
  ].filter(Boolean), [APP_URL]);


  useEffect(() => {
    if (newDivRef.current) {
      newDivRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Copied to clipboard:', text);
    }, (err) => {
      console.error('Error copying to clipboard:', err);
    });
  };

  const handleQuery = async (type: string, previousQuery: string = '') => {
    console.log(`handleQuery called — type=${type}, previousQuery=${previousQuery}`);
    setLoader(true);
    let query = highlightSection;
    const numberRegEx = /([0-9]*[.])?([0-9]*[.])?[0-9]+/;
    const sectionNumberIndex = query.search(numberRegEx);

    // See if section number was included by checking if the regular expression returns result at 0, or front of string
    if (sectionNumberIndex === 0) {
      query = query.replace(numberRegEx, '').trim();
    }

    // Fake message sent by user
    const cfg = QUERY_CONFIG[type];
    const fakeMessage = cfg.fakeMessage;
    const body = [{
      text: fakeMessage,
      expanded: false
    }];
    setMessages(prevMessages => [
      ...prevMessages,
      { sender: 'user', type, body, query }
    ]);
    console.log(`Added fake user message for type=${type}`);

    // Check if same query is being sent and use the stored response
    if (previousQuery && previousQuery === query) {
      console.log(`Returning early: previousQuery ("${previousQuery}") matches current query ("${query}")`);
      setLoader(false);
      addMessages(type, recentResponse, query);
      return;
    }
    // Check if the token is generated or not
    if (cfg.requiresAuth && !isAuthorized) {
      console.log('User not authorized');
      setLoader(false);
      return;
    }
    try {
          const payload:any = cfg.buildPayload(query, trialUuid);
          const headers: HeadersInit = { "Content-Type": "application/json" };
          if (cfg.requiresAuth) {
              headers["Authorization"] = `Bearer ${accessTokenRef.current}`;
          }
          const response = await fetch(cfg.endpoint, {
            method: "POST",
            headers,
            body: JSON.stringify(payload)
          });

          const data = await response.json();
          if (response.ok) {
            setRecentResponse(data);
            addMessages(type, data, query);
          } else {
            console.error('Error encountered while generating the query');
            addMessages(type, data, '');
          }
    } catch (error) {
        const body = [{
          text: 'Network error. Please try again later.',
          expanded: false
        }];
        console.log('API call failed: ', error);
        setMessages(prevMessages => [...prevMessages, { sender: 'bot', type, body, query: '' }]);
    } finally {
        setLoader(false);
        console.log('Chat was processed successfully');
    }
  };

  const addMessages = (type, data, query) => {
  let body: MessageBody[] = [];
  console.log('Query passed',query);
  if (type === "generate") {
    console.log( `Generated data: ${data} for type: ${type}`);
    const text = data?.answer ?? data?.detail ?? '';
    body = [
      {
        text,
        expanded: false
      }
    ];
  }

  else if (type === "template") {
    console.log( `Generated data: ${data} for type: ${type}`);
    const templates = data[query] || [];
    body = templates.map(t => ({ text: t.template }));
  }
  else if (type === "references") {
    console.log( `Generated data: ${data} for type: ${type}`);
    const trials = data.trials || [];
      if (trials.length === 0) {
      body = [{
          text: "There is no historical data",
          header: "",
          sponsor: "",
          expanded: false
        }];
    } else {
      body = trials.map(trial => ({
        header: trial.id,
        sponsor: trial.sponsor,
        text: trial.chunk_content,
        expanded: false
      }));
    }
  }else {
    console.log('No type matched');
    const docs = data.retrieved_documents || [];
    body = docs.map(doc => {
      const idx = doc.indexOf("\n");
      return {
        header: doc.substring(0, idx),
        text: doc.substring(idx),
        expanded: false
      };
    });
  }

  setMessages(prevMessages => [
    ...prevMessages,
    { sender: "bot", type, body, query }
  ]);
  };

  const handleDocumentHighlight = async () => {
    Office.context.document.getSelectedDataAsync(
      Office.CoercionType.Text,
      (selection: any) => {
        if (selection.value) {
          const highlighted = selection.value.toLowerCase();
          setHighlightSection(highlighted);
          setIsHighlighted(true); // Set to true when text is highlighted
        } else {
          setIsHighlighted(false); // Set to false if no text is highlighted
        }
      }
    );
  };

  const handlePostMessage = (event) => {
     if (!TRUSTED_ORIGINS.includes(event.origin)) {
      console.log('Blocked message from untrusted origin', event.origin);
      return;
    }

    let msg;
    try {
      msg = JSON.parse(event.data);
    } catch (error) {
      console.log('Failed to parse message data:', error);
      return;
    }
    console.log('Received post message:', msg);
    const msgId = msg.MessageId;
    const msgData = msg.Values;


    if (msgId === 'Host_TrialData' && msgData.trial_uuid && msgData.access_token) {
        console.log('Received Host_TrialData message with trial ID');
        accessTokenRef.current = msgData.access_token;
        setTrialUuid(msgData.trial_uuid);
        setIsAuthorized(true);
    }
  };

  useEffect(() => {
    Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, handleDocumentHighlight);
    window.addEventListener('message', handlePostMessage, false);
    window.top.postMessage('addInLoaded', '*');
     return () => {
      window.removeEventListener('message', handlePostMessage, false);
    };
  }, []);

  const insertAllText = (message: string) => {
    // Strip the first leading heading — the document already has the section
    // header so we only need the body content. Intentionally non-global: only
    // the top-level section heading is redundant; any subsequent headings
    // (e.g. sub-section titles) are part of the generated content.
    const content = (message ?? '').replace(/^\s*<h[1-6][^>]*>[\s\S]*?<\/h[1-6]>\s*/i, '');

    if (!content.trim()) {
      console.warn('insertAllText: generated content was heading-only, nothing to insert');
      return;
    }

    // Insert the message into the Word document
    Office.context.document.setSelectedDataAsync(
      content,
      { coercionType: Office.CoercionType.Html },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          setIsHighlighted(false);
          console.log("Message inserted into Word document successfully.");
        } else {
          console.error("Failed to insert message into Word document:", result.error.message);
        }
      }
    );
  };

  const toggleExpand = (messageIndex) => {
    setExpandedMessages(prevExpanded => {
      const newExpanded = new Set(prevExpanded);
      if (newExpanded.has(messageIndex)) {
        newExpanded.delete(messageIndex);
      } else {
        newExpanded.add(messageIndex);
      }
      return newExpanded;
    });
  };

  return (
    <div className={styles.chatContainer}>
      <div>
        <div>
          <img src={require('../../../assets/ai.png')} alt="AI Logo" style={{ width: '30px', height: '30px', marginRight: '5px' }} />
          IntelliScribe
        </div>
        <div className={styles.content}>
          Welcome to IntelliScribe!<br></br>
          Your intelligent protocol authoring assistant is here to help.
        </div>
        <button className={styles.insertToDocumentButton} onClick={() => handleQuery('generate')} disabled={isLoading || !isHighlighted}>
          {GENERATE_TEXT}
        </button>
        <br />
        <button className={styles.insertToDocumentButton} onClick={() => handleQuery('references')} disabled={isLoading || !isHighlighted}>
          {HISTORICAL_REFERENCE_TEXT}
        </button>
      </div>
      <div className={styles.messagesContainer}>
        {messages.map((message, index) => (
          <div key={index}>
            {message.sender === 'bot' &&
              <>
                <img src={require('../../../assets/ai.png')} alt="AI Logo" style={{ width: '30px', height: '30px', marginRight: '5px' }} />
                IntelliScribe
                {message.type === 'generate'
                  ? <p className={styles.content}>Here is what I generated based off your highlighted selection</p>
                  : <p className={styles.content}>Here are some historical references I found</p>
                }
              </>
            }
            {message.body && message.body.map((item, bodyIndex) => {
              const words = item.text.split(' ');
              const displayText = item.expanded ? item.text : words.slice(0, MAX_WORDS).join(' ');
              return (
                <div key={bodyIndex} className={message.sender === 'bot' ? styles.copilotMessage : styles.youMessage}>
                  {item.header && <strong><a 
                  href={item.header.startsWith("NCT")
                    ? `https://clinicaltrials.gov/study/${item.header}`
                    : `https://euclinicaltrials.eu/search-for-clinical-trials/?lang=en&EUCT=${item.header}`}
                  target="_"> {item.header} </a></strong>}
                    <div className={styles.previewWrapper}>
                      <div className={item.expanded ? styles.expandedContainer : styles.container}>
                        <div className={styles.sponsor}>{item.sponsor} </div>
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(displayText) }} />
                        {!item.expanded && message.sender === "bot" && words.length > MAX_WORDS && <div className={styles.fadeGradient} />}
                      </div>

                      {message.sender === "bot" && words?.length > MAX_WORDS && 
                        <span
                          className={styles.viewToggle}
                          onClick={() => {
                            setMessages((prevMessages) =>
                              prevMessages.map((msg, msgIdx) => {
                                if (msgIdx !== index) return msg;
                                return {
                                  ...msg,
                                  body: msg.body.map((b, bIdx) => {
                                    if (bIdx !== bodyIndex) return b;
                                    return { ...b, expanded: !b.expanded };
                                  }),
                                };
                              })
                            );
                          }}
                        >
                          {item.expanded ? "View less" : "View all"}
                        </span>
                      }
                    </div>
                    
                  {message.sender === 'bot' &&
                    <div className={styles.buttonGroup}>
                      <Button className={styles.insertButton} icon={<FileAddOutlined />} onClick={() => insertAllText(item.text)}>Insert</Button>
                      <Button className={styles.insertButton} icon={<CopyOutlined />} onClick={() => handleCopy(item.text)}>Copy</Button>
                    </div>
                  }
                </div>
              );
            })}
            {message.sender === 'bot' &&
              <>
                <button className={styles.insertToDocumentButton} onClick={() => handleQuery('generate', message.query)} disabled={isLoading || !isHighlighted}>
                  {GENERATE_TEXT}
                </button>
                <br />
                <button className={styles.insertToDocumentButton} onClick={() => handleQuery('references', message.query)} disabled={isLoading || !isHighlighted}>
                  {HISTORICAL_REFERENCE_TEXT}
                </button>
              </>
            }
          </div>
        ))}
        {isLoading && <><Spin spinning={isLoading} size={'small'} indicator={<LoadingOutlined style={{ color: "#ca4a04" }} />}></Spin><span> Thinking...</span></>}
      </div>
    </div>
  );
}

export default ChatBot;
